/**
 * pipelineJSON.ts - Pipeline JSON Export/Import Utilities
 * 
 * CHANGE HISTORY:
 * - 251128_1230 danielbeer: Initial creation
 * 
 * Utilities for exporting and importing pipeline configurations as JSON
 * with metadata, schemas, and data values.
 */

import type {
  MapperPipeline,
  DataSet,
  Field,
  FieldMapping,
  PipelineJSON,
  DataSetJSON,
  FieldSchema,
  TransferMeta,
  DataSetMeta,
  FieldValueOverride
} from '../types/index';

/**
 * Export pipeline to JSON format
 */
export function exportPipelineToJSON(pipeline: MapperPipeline): PipelineJSON {
  const now = new Date().toISOString();
  
  // Create transfer metadata
  const transferMeta: TransferMeta = {
    jsonVersion: '1.0.0',
    arrayDefinedDate: now,
    datasetSource: 0,
    datasetDestination: pipeline.datasets.length - 1
  };
  
  // Convert each dataset
  const datasets: DataSetJSON[] = pipeline.datasets.map((dataset) => {
    return exportDataSetToJSON(dataset, dataset.position);
  });
  
  return {
    transferMeta,
    datasets
  };
}

/**
 * Export a single dataset to JSON format
 */
export function exportDataSetToJSON(dataset: DataSet, _index?: number): DataSetJSON {
  const now = new Date().toISOString();
  
  // Create dataset metadata
  const datasetMeta: DataSetMeta = {
    source: dataset.meta?.source || {
      type: 'variable',
      variable: dataset.name
    },
    version: dataset.meta?.version || '1.0.0',
    asAtUTC: dataset.meta?.asAtUTC || now
  };
  
  // Create field schemas
  const schema: FieldSchema[] = dataset.fields.map(field => {
    // Find mapping for this field to get value override
    const mapping = dataset.mappings.find(m => m.targetFieldId === field.id);
    const valueOverride = mapping?.valueOverride || dataset.defaultValueOverride || 'overwrite';
    
    return {
      name: field.name,
      value: field.sample,
      type: field.type,
      options: {
        valueOverride,
        required: field.required,
        defaultValue: field.sample
      }
    };
  });
  
  // Extract data values (array of arrays)
  const values: any[][] = [];
  if (dataset.data && dataset.data.length > 0) {
    dataset.data.forEach(record => {
      const row: any[] = dataset.fields.map(field => {
        return record[field.name] !== undefined ? record[field.name] : null;
      });
      values.push(row);
    });
  }
  
  return {
    datasetMeta,
    data: {
      schema,
      values
    }
  };
}

/**
 * Import pipeline from JSON format
 */
export function importPipelineFromJSON(json: PipelineJSON): MapperPipeline {
  const datasets: DataSet[] = json.datasets.map((datasetJSON, index) => {
    return importDataSetFromJSON(datasetJSON, index);
  });
  
  return {
    datasets,
    showFieldTypes: true,
    showSampleData: true,
    compact: false
  };
}

/**
 * Import a single dataset from JSON format
 */
export function importDataSetFromJSON(datasetJSON: DataSetJSON, position: number): DataSet {
  const { datasetMeta, data } = datasetJSON;
  
  // Convert schema to fields
  const fields: Field[] = data.schema.map((fieldSchema, index) => ({
    id: `field-${position}-${index}`,
    name: fieldSchema.name,
    type: fieldSchema.type,
    required: fieldSchema.options?.required || false,
    sample: fieldSchema.value
  }));
  
  // Convert values to data records
  const records: any[] = data.values.map(row => {
    const record: any = {};
    data.schema.forEach((fieldSchema, index) => {
      record[fieldSchema.name] = row[index];
    });
    return record;
  });
  
  // Create mappings (initially unmapped)
  const mappings: FieldMapping[] = fields.map(field => {
    const fieldSchema = data.schema.find(s => s.name === field.name);
    
    return {
      id: `mapping-${field.id}`,
      sourceFieldId: null,
      targetFieldId: field.id,
      enabled: false,
      hasMatch: false,
      valueOverride: fieldSchema?.options?.valueOverride
    };
  });
  
  return {
    id: `dataset-${position}`,
    name: datasetMeta.source.variable || `Dataset ${position}`,
    fields,
    data: records.length > 0 ? records : undefined,
    position,
    mappings,
    meta: datasetMeta
  };
}

/**
 * Apply value override rules when mapping data
 */
export function applyValueOverride(
  sourceValue: any,
  destinationValue: any,
  override: FieldValueOverride = 'overwrite'
): any {
  switch (override) {
    case 'overwrite':
      // Source overwrites destination (default)
      return sourceValue !== undefined && sourceValue !== null ? sourceValue : destinationValue;
    
    case 'preserveDestination':
      // Keep destination if it exists
      return destinationValue !== undefined && destinationValue !== null ? destinationValue : sourceValue;
    
    case 'alwaysSetSource':
      // Clear destination if no source
      return sourceValue !== undefined && sourceValue !== null ? sourceValue : null;
    
    case 'skipIfEmpty':
      // Skip field if no source or destination
      if ((sourceValue === undefined || sourceValue === null) && 
          (destinationValue === undefined || destinationValue === null)) {
        return undefined; // Skip this field
      }
      return sourceValue !== undefined && sourceValue !== null ? sourceValue : destinationValue;
    
    default:
      return sourceValue;
  }
}

/**
 * Validate pipeline JSON structure
 */
export function validatePipelineJSON(json: any): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!json.transferMeta) {
    errors.push('Missing transferMeta');
  }
  
  if (!json.datasets || !Array.isArray(json.datasets)) {
    errors.push('Missing or invalid datasets array');
  } else {
    json.datasets.forEach((dataset: any, index: number) => {
      if (!dataset.datasetMeta) {
        errors.push(`Dataset ${index}: Missing datasetMeta`);
      }
      
      if (!dataset.data || !dataset.data.schema || !Array.isArray(dataset.data.schema)) {
        errors.push(`Dataset ${index}: Missing or invalid data.schema`);
      }
      
      if (!dataset.data || !dataset.data.values || !Array.isArray(dataset.data.values)) {
        errors.push(`Dataset ${index}: Missing or invalid data.values`);
      }
    });
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Create a sample pipeline JSON for testing
 */
export function createSamplePipelineJSON(): PipelineJSON {
  return {
    transferMeta: {
      jsonVersion: '1.0.0',
      arrayDefinedDate: new Date().toISOString(),
      datasetSource: 0,
      datasetDestination: 1
    },
    datasets: [
      {
        datasetMeta: {
          source: {
            type: 'table',
            field: {
              filename: 'contacts.fmp12',
              baseTable: 'Contacts',
              tableOccurrence: 'Contacts'
            }
          },
          version: '1.0.0',
          asAtUTC: new Date().toISOString()
        },
        data: {
          schema: [
            { name: 'firstName', value: 'John', type: 'string', options: { valueOverride: 'overwrite' } },
            { name: 'lastName', value: 'Doe', type: 'string', options: { valueOverride: 'overwrite' } },
            { name: 'email', value: 'john@example.com', type: 'email', options: { valueOverride: 'overwrite' } }
          ],
          values: [
            ['John', 'Doe', 'john@example.com'],
            ['Jane', 'Smith', 'jane@example.com']
          ]
        }
      },
      {
        datasetMeta: {
          source: {
            type: 'table',
            field: {
              filename: 'customers.fmp12',
              baseTable: 'Customers',
              tableOccurrence: 'Customers'
            }
          },
          version: '1.0.0',
          asAtUTC: new Date().toISOString()
        },
        data: {
          schema: [
            { name: 'FullName', value: '', type: 'string', options: { valueOverride: 'overwrite' } },
            { name: 'EmailAddress', value: '', type: 'email', options: { valueOverride: 'overwrite' } }
          ],
          values: []
        }
      }
    ]
  };
}
